import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Button from "common/components/Button/Button";
import useAuth from "common/hooks/useAuth";
import { ObservationResponse } from "types/ObservationResponse";
import ImageComponent from "common/components/ImageComponent/ImageComponent";
import ImageFromStrapiMedia from "common/components/ImageFromStrapiMedia/ImageFromStrapiMedia";
import { PiPawPrintFill } from "react-icons/pi";
import { UserIcon } from "@heroicons/react/24/solid";
import { CalendarDaysIcon } from "@heroicons/react/20/solid";
import dayjs from "dayjs";
import exportData from "common/utils/exportData";
import { TableCellsIcon } from "@heroicons/react/24/outline";

function ObservationResponseItem({ item, onNavigate }: any) {
  const {
    id,
    animal,
    animalName,
    animalLocalId,
    animalGender,
    animalSpeciesScientificName,
    observationGroupName,
    asignedUser,
    assignedUserUsername,
    observationDateTime,
    observationGrid,
  } = item as ObservationResponse;

  const { user: currentUser } = useAuth();
  const { t } = useTranslation();

  const canSeeDetailsFromItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park?.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park?.id))) ??
    false;
  const detailLink = `/observation-responses/${id}`;

  return (
    <div className="responsive-flex-lg px-4 py-4 mt-2 w-full relativ cursor-pointer rounded-lg border border-slate-150 transition-all bg-white">
      <Link
        to={detailLink}
        className={
          !canSeeDetailsFromItem
            ? "pointer-events-none responsive-flex-lg flex-1"
            : "responsive-flex-lg flex-1"
        }
      >
        <div className="flex relative w-40 h-40 lg:w-16 lg:h-16 shrink-0">
          <div className="rounded-lg overflow-hidden w-full">
            <ImageComponent
              image={
                animal?.picture
                  ? ImageFromStrapiMedia(animal.picture)?.uri
                  : undefined
              }
            />
          </div>
        </div>
        <div className="flex flex-col flex-1 justify-between pl-4 pr-6 mb-2">
          <div className="flex gap-3">
            <div className="flex-1 flex flex-col gap-1">
              <div className="flex-1 flex flex-row gap-2 py-3 lg:py-0">
                <PiPawPrintFill />
                <h4 className="text-xl font-bold text-primary leading-4">
                  {animal?.isGroup && `${t("common.individualsGroup")} : `}
                  {animal?.houseName ??
                    animalName ??
                    animal?.localId ??
                    animalLocalId ??
                    animal?.zimsId ??
                    animal?.transponderId ??
                    animal?.tag ??
                    animal?.band ??
                    observationGroupName ??
                    t("common.unnnamed")}
                </h4>

                {animalSpeciesScientificName ? (
                  <div className="flex flex-row items-center text-sm">
                    <span className="font-bold">
                      {animalSpeciesScientificName}
                    </span>
                  </div>
                ) : null}

                {animal?.zimsId && (
                  <div className="flex items-center gap-1 text-sm">
                    <span className="font-bold">{`ID: ${animal?.zimsId}`}</span>
                  </div>
                )}
                {(animal?.gender || animalGender) && (
                  <div className="px-2 rounded-md bg-green-100 text-green-800 border border-green-500 flex flex-col justify-center items-center text-sm w-fit">
                    {t(`animals.${animal?.gender ?? animalGender}`)}
                  </div>
                )}
              </div>
              <div className="flex-1 flex flex-row gap-2 py-3 lg:py-0 items-center">
                <UserIcon className="w-4 h-4" />

                <div className="flex items-center gap-1 text-sm">
                  <span className="font-bold text-base">
                    {asignedUser?.username ?? assignedUserUsername}
                  </span>
                </div>
              </div>
              <div className="flex-1 flex flex-row gap-2 py-3 lg:py-0 items-center">
                <CalendarDaysIcon className="w-4 h-4" />

                <div className="flex items-center gap-1 text-sm">
                  <span className="font-bold text-base">
                    {dayjs(observationDateTime).format("LLL")}
                  </span>
                </div>
              </div>
              {observationGrid && (
                <div className="flex-1 flex flex-row gap-2 py-3 lg:py-0 items-center">
                  <TableCellsIcon className="w-4 h-4" />

                  <div className="flex items-center gap-1 text-sm">
                    <span className="font-bold text-base">
                      {observationGrid?.name}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>

      <div className="flex flex-col justify-between items-end text-xs lg:border-l pl-4 lg:min-w-[22em] border-slate-150 gap-2">
        <div className="flex w-full items-center justify-end text-slate-500 gap-1">
          <Button
            title={t("actions.see")}
            type="warning-line"
            icon="EyeIcon"
            onClick={() => onNavigate(item.id)}
            disabled={!canSeeDetailsFromItem}
          />
        </div>
        <div className="flex w-full items-center justify-end text-slate-500 gap-1">
          <Button
            title={t("actions.export")}
            type="neutral-line"
            icon="AiOutlineDownload"
            onClick={() =>
              exportData({
                importType: "observationGrid",
                id,
                lang: currentUser?.language,
              })
            }
            disabled={!canSeeDetailsFromItem}
          />
        </div>
      </div>
    </div>
  );
}

export default ObservationResponseItem;
